import React, { useState, useEffect, useRef } from 'react';
import { 
  Truck, Settings, 
  Bell, LogOut, ChevronDown, ChevronLeft, ChevronRight, User, Briefcase, Train, Map, Plane, ArrowLeft,
  Search, Grid, Star, History, Layout, Globe, ShieldCheck, Building2, Landmark, Ship,
  FileText, BarChart3, Wallet, CreditCard, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import sidebarBackground from '../assets/sidebar_background_custom.svg';

const normalizeFa = (value = '') =>
  String(value)
    .trim()
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, '')
    .toLowerCase();

const DIRECT_AND_ACCESS_SUBMENU_ITEMS = [
  { title: 'رهگیری چندوجهی (دریایی/ریلی/جاده‌ای/هوایی)', icon: Map },
  { title: 'حمل دریایی / ریلی / جاده‌ای / هوایی (کارگو)', icon: Truck },
  { title: 'راهنمای اسناد / نمونه فرم‌ها / چک‌لیست‌ها', icon: FileText },
  { title: 'ثبت درخواست', icon: ArrowLeft },
  { title: 'تعرفه‌ها / الزامات / پرسش‌های پرتکرار', icon: ShieldCheck },
  { title: 'دایرکتوری شرکت‌ها / RFQ', icon: Building2 },
  { title: 'مرور پروژه‌ها / فیلتر و مقایسه', icon: Search },
  { title: 'مسیرهای اخذ مجوز / مدل‌های مشارکت', icon: Landmark },
  { title: 'ثبت علاقه‌مندی / درخواست جلسه', icon: Star },
  { title: 'داشبورد پروژه‌ها', icon: Layout },
  { title: 'دریافت اطلاعات پروژه', icon: FileText },
  { title: 'اعلام علاقه‌مندی سرمایه‌گذاری', icon: Star },

  { title: 'دریایی', icon: Ship },
  { title: 'ریلی', icon: Train },
  { title: 'جاده‌ای', icon: Truck },
  { title: 'هوایی (کارگو)', icon: Plane },
  { title: 'فیلتر و مقایسه خدمات', icon: Search },
  { title: 'ثبت سفارش / پیگیری سفارش', icon: History },
  { title: 'کیف پول لجستیکی', icon: Wallet },
  { title: 'پرداخت‌ها', icon: CreditCard },
  { title: 'فاکتورها و صورتحساب‌ها', icon: FileText },
  { title: 'تسویه حساب', icon: RefreshCw },
  { title: 'گزارش مالی', icon: BarChart3 },
];

const POPULAR_SUBMENU_TITLES = [
  'رهگیری چندوجهی (دریایی/ریلی/جاده‌ای/هوایی)',
  'حمل دریایی / ریلی / جاده‌ای / هوایی (کارگو)',
  'ثبت درخواست',
  'ثبت سفارش / پیگیری سفارش',
  'پرداخت‌ها',
];

const SIDEBAR_BACKGROUND_CLASS =
  "bg-[#2E5AA6] before:content-[''] before:absolute before:inset-0 before:z-[1] before:pointer-events-none before:bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.12),transparent_60%)] before:opacity-55 after:content-[''] after:absolute after:inset-0 after:z-[1] after:pointer-events-none after:bg-[linear-gradient(135deg,rgba(255,255,255,0.06)_1px,transparent_1px)] after:bg-[length:26px_26px] after:opacity-16";

const MENU_BACKGROUND_CLASS =
  "bg-gradient-to-b from-[#1A3062] via-[#244789] to-[#1A3062] before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.12),transparent_60%)] before:opacity-100 after:content-[''] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,rgba(255,255,255,0.06)_1px,transparent_1px)] after:bg-[length:26px_26px] after:opacity-40";

const NOTIFICATIONS_SESSION_KEY = 'corridor.notifications.session.v1';

const NOTIFICATION_CATALOG = [
  {
    id: 'n-1',
    title: 'یادآوری تکمیل پروفایل',
    body: 'برای فعال شدن همه سرویس‌ها، اطلاعات هویتی و شماره همراه را تکمیل کنید.'
  },
  {
    id: 'n-2',
    title: 'وضعیت درخواست جدید',
    body: 'یک درخواست جدید ثبت شده و آماده بررسی در کارتابل شماست.'
  },
  {
    id: 'n-3',
    title: 'به‌روزرسانی تعرفه‌ها',
    body: 'تعرفه‌های حمل و نقل به‌روزرسانی شد. قبل از ثبت سفارش بررسی کنید.'
  },
  {
    id: 'n-4',
    title: 'هشدار پرداخت',
    body: 'یک پرداخت معوق دارید. برای جلوگیری از توقف فرایند، تسویه را انجام دهید.'
  },
  {
    id: 'n-5',
    title: 'رهگیری محموله',
    body: 'وضعیت رهگیری چندوجهی محموله شما تغییر کرده است. جزئیات را مشاهده کنید.'
  },
  {
    id: 'n-6',
    title: 'اسناد موردنیاز',
    body: 'برای ادامه فرایند، یک سند/فرم از لیست چک‌لیست‌ها بارگذاری نشده است.'
  },
  {
    id: 'n-7',
    title: 'یادآوری جلسه',
    body: 'درخواست جلسه/مشاوره ثبت شده است. زمان پیشنهادی را تأیید یا تغییر دهید.'
  },
  {
    id: 'n-8',
    title: 'فرصت سرمایه‌گذاری جدید',
    body: 'یک فرصت سرمایه‌گذاری جدید مطابق علاقه‌مندی‌های شما اضافه شد.'
  },
  {
    id: 'n-9',
    title: 'به‌روزرسانی قوانین و راهنما',
    body: 'قوانین و راهنماهای مرتبط با تجارت/ترخیص به‌روزرسانی شد.'
  },
  {
    id: 'n-10',
    title: 'پیام پشتیبانی',
    body: 'پاسخ جدیدی از پشتیبانی دریافت کردید. برای مشاهده وارد پیام‌ها شوید.'
  }
];

function pickRandomUnique(items, count) {
  const arr = Array.isArray(items) ? [...items] : [];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, Math.max(0, Math.min(count, arr.length)));
}

function formatJalaliNow(date) {
  try {
    return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('fa-IR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    }).format(date);
  }
}

function formatGregorian(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}`;
}

const Dashboard = () => {
  const [openMenus, setOpenMenus] = useState({ access: true, direct: true });
  const [activeTab, setActiveTab] = useState('identity');
  const [currentUser, setCurrentUser] = useState(null);
  const [quickAccessCollapsed, setQuickAccessCollapsed] = useState(false);
  const [submenuSearchQuery, setSubmenuSearchQuery] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sessionNotifications, setSessionNotifications] = useState([]);
  const notifBoxRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('current_user'));
    if (user) {
      setCurrentUser(user);
    }
  }, []);

  useEffect(() => {
    let payload = null;
    try {
      payload = JSON.parse(sessionStorage.getItem(NOTIFICATIONS_SESSION_KEY));
    } catch {
      payload = null;
    }

    if (!payload || !Array.isArray(payload.items)) {
      const count = Math.floor(Math.random() * 3) + 1;
      const picked = pickRandomUnique(NOTIFICATION_CATALOG, count).map((n) => ({
        ...n,
        createdAt: Date.now(),
      }));
      payload = { items: picked, read: false };
      sessionStorage.setItem(NOTIFICATIONS_SESSION_KEY, JSON.stringify(payload));
    }

    setSessionNotifications(payload.items.slice(0, 3));
    setUnreadCount(payload.read ? 0 : payload.items.slice(0, 3).length);
    return undefined;
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!notificationsOpen) return;
      const el = notifBoxRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setNotificationsOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [notificationsOpen]);

  const markNotificationsRead = () => {
    setUnreadCount(0);
    setNotificationsOpen(false);
    try {
      const existing = JSON.parse(sessionStorage.getItem(NOTIFICATIONS_SESSION_KEY));
      if (existing && Array.isArray(existing.items)) {
        sessionStorage.setItem(
          NOTIFICATIONS_SESSION_KEY,
          JSON.stringify({ ...existing, read: true })
        );
      }
    } catch {
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('current_user');
    } catch {
    }

    try {
      sessionStorage.removeItem(NOTIFICATIONS_SESSION_KEY);
    } catch {
    }

    setNotificationsOpen(false);
    setCurrentUser(null);
    window.location.assign('/');
  };

  const toggleMenu = (key) => {
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const normalizedQuery = normalizeFa(submenuSearchQuery);
  const submenuSearchResults =
    normalizedQuery.length >= 1
      ? DIRECT_AND_ACCESS_SUBMENU_ITEMS.filter((item) =>
          normalizeFa(item.title).includes(normalizedQuery)
        )
      : [];

  const topFivePopularItems = POPULAR_SUBMENU_TITLES
    .map((t) => DIRECT_AND_ACCESS_SUBMENU_ITEMS.find((x) => x.title === t))
    .filter(Boolean);

  return (
    <div className="flex min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-4 gap-6" dir="rtl">
        <aside
          className={`w-[290px] text-white flex flex-col shadow-2xl z-20 sticky top-4 h-[calc(100vh-2rem)] overflow-y-auto no-scrollbar rounded-[2.5rem] border border-white/10 relative overflow-hidden ${SIDEBAR_BACKGROUND_CLASS}`}
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(26,48,98,0.50), rgba(26,48,98,0.50)), url(${sidebarBackground})`,
            backgroundSize: '100% auto',
            backgroundPosition: 'top center',
            backgroundRepeat: 'repeat-y',
            backgroundAttachment: 'local',
          }}
        >
          
          <div className="p-8 text-center border-b border-white/10 relative z-10">
          <div className="flex items-center justify-center gap-3 mb-2">
             <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/10">
                <img
                  src="/logo.png"
                  alt="لوگو"
                  width={24}
                  height={24}
                  className="w-6 h-6 rounded-full object-cover"
                  style={{
                    opacity: 0.92,
                    filter: 'saturate(0.85) brightness(1.06) contrast(1.06)',
                  }}
                />
             </div>
             <div className="text-center">
                <h2 className="text-lg font-bold leading-none">کریدور هوشمند</h2>
                <p className="text-[11px] text-blue-200 mt-1">شمال - جنوب</p>
             </div>
          </div>

          <div className="mt-5 flex items-center justify-center">
            <div className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 transition rounded-xl px-4 py-2 border border-white/15 backdrop-blur-sm">
              <User size={16} className="text-white/80" />
              <span className="text-[11px] font-bold">
                {currentUser?.type === 'legal' ? 'حقوقی' : 'حقیقی'} - احراز هویت سطح ۲
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 py-6 px-4 space-y-1 relative z-10">
          
          <button className="w-full bg-yellow-400 hover:bg-yellow-500 text-[#1A3062] rounded-xl p-3.5 font-bold flex items-center justify-between shadow-lg cursor-pointer transition-all mb-6">
             <div className="flex items-center gap-3">
                <Layout size={20} />
                <span>داشبورد</span>
             </div>
             <div className="bg-[#1A3062]/10 p-1 rounded-md">
                <User size={14} />
             </div>
          </button>

          <NavItem text="حساب کاربری" icon={<User size={18}/>} onClick={() => setActiveTab('identity')} />
          <NavItem text="مدیریت کارتابل" icon={<Briefcase size={18}/>} />
          
          <div className="py-2">
             <button onClick={() => toggleMenu('access')} className="flex items-center justify-between w-full p-3 text-sm text-white/80 hover:text-white hover:bg-white/5 rounded-xl transition shadow-[0_3px_14px_rgba(0,0,0,0.48)] hover:shadow-[0_4px_18px_rgba(0,0,0,0.60)]">
                <span className="flex items-center gap-3"><Grid size={18}/> دسترسی به سامانه‌ها</span>
                <ChevronDown size={16} className={`transform transition duration-300 ${openMenus.access ? 'rotate-180' : ''}`} />
             </button>
             <AnimatePresence>
               {openMenus.access && (
                 <motion.div 
                   initial={{ height: 0, opacity: 0 }}
                   animate={{ height: 'auto', opacity: 1 }}
                   exit={{ height: 0, opacity: 0 }}
                   className="overflow-hidden mr-4 mt-1 space-y-1"
                 >
                    <SubMenuLabel text="رهگیری و عملیات" />
                    <SubNavItem level={2} text="رهگیری چندوجهی (دریایی/ریلی/جاده‌ای/هوایی)" icon={<Map size={14} />} />
                    <SubMenuLabel text="بازارچه خدمات لجستیکی" />
                    <SubNavItem level={2} text="حمل دریایی / ریلی / جاده‌ای / هوایی (کارگو)" icon={<Truck size={14} />} />
                    <SubMenuLabel text="اسناد و فرم‌ها" />
                    <SubNavItem level={2} text="راهنمای اسناد / نمونه فرم‌ها / چک‌لیست‌ها" icon={<FileText size={14} />} />

                    <div className="p-2 pr-4 text-[10px] text-white/40 font-bold mt-2">تجارت</div>
                    <SubNavItem level={2} text="ثبت درخواست" icon={<ArrowLeft size={14} />} />
                    <SubNavItem level={2} text="تعرفه‌ها / الزامات / پرسش‌های پرتکرار" icon={<ShieldCheck size={14} />} />
                    <SubNavItem level={2} text="دایرکتوری شرکت‌ها / RFQ" icon={<Building2 size={14} />} />

                    <div className="p-2 pr-4 text-[10px] text-white/40 font-bold mt-2">سرمایه‌گذاری</div>
                    <SubNavItem level={2} text="مرور پروژه‌ها / فیلتر و مقایسه" icon={<Search size={14} />} />
                    <SubMenuLabel text="مشوق‌ها و مجوزها (پیشنهادی)" />
                    <SubNavItem level={2} text="مسیرهای اخذ مجوز / مدل‌های مشارکت" icon={<Landmark size={14} />} />
                    <SubNavItem level={2} text="ثبت علاقه‌مندی / درخواست جلسه" icon={<Star size={14} />} />
                 </motion.div>
               )}
             </AnimatePresence>
          </div>

          <div className="py-2">
             <button onClick={() => toggleMenu('direct')} className="flex items-center justify-between w-full p-3 text-sm text-white/80 hover:text-white hover:bg-white/5 rounded-xl transition shadow-[0_3px_14px_rgba(0,0,0,0.48)] hover:shadow-[0_4px_18px_rgba(0,0,0,0.60)]">
                <span className="flex items-center gap-3"><Truck size={18}/> خدمات مستقیم</span>
                <ChevronDown size={16} className={`transform transition duration-300 ${openMenus.direct ? 'rotate-180' : ''}`} />
             </button>
             <AnimatePresence>
               {openMenus.direct && (
                 <motion.div 
                   initial={{ height: 0, opacity: 0 }}
                   animate={{ height: 'auto', opacity: 1 }}
                   exit={{ height: 0, opacity: 0 }}
                   className="overflow-hidden mr-4 mt-1 space-y-1"
                 >
                    <div className="p-2 pr-4 text-[10px] text-white/40 font-bold">بازارچه خدمات</div>
                    <SubNavItem level={2} text="دریایی" icon={<Ship size={14} />} />
                    <SubNavItem level={2} text="ریلی" icon={<Train size={14} />} />
                    <SubNavItem level={2} text="جاده‌ای" icon={<Truck size={14} />} />
                    <SubNavItem level={2} text="هوایی (کارگو)" icon={<Plane size={14} />} />
                    <SubNavItem text="فیلتر و مقایسه خدمات" icon={<Search size={14} />} desc="نوع حمل / زمان / هزینه" />
                    <SubMenuLabel text="سفارش‌ها" />
                    <SubNavItem level={2} text="ثبت سفارش / پیگیری سفارش" icon={<History size={14} />} />

                    <div className="p-2 pr-4 text-[10px] text-white/40 font-bold mt-2">مالی و تسویه</div>
                    <SubNavItem text="کیف پول لجستیکی" icon={<Wallet size={14} />} desc="شارژ/برداشت (پیشنهادی)" />
                    <SubNavItem text="پرداخت‌ها" icon={<CreditCard size={14} />} desc="پرداخت‌های موفق/معلق" />
                    <SubNavItem text="فاکتورها و صورتحساب‌ها" icon={<FileText size={14} />} desc="دانلود / چاپ / آرشیو" />
                    <SubNavItem text="تسویه حساب" icon={<RefreshCw size={14} />} desc="تسویه جدید / وضعیت تسویه" />
                    <SubNavItem text="گزارش مالی" icon={<BarChart3 size={14} />} />
                 </motion.div>
               )}
             </AnimatePresence>
          </div>

          <NavItem text="مشارکت الکترونیکی" icon={<Globe size={18}/>} />
          
          <div className="pt-4 mt-4 border-t border-white/5 space-y-1">
            <NavItem text="سریع و کاربردی" icon={<Star size={18}/>} />
            <NavItem text="علاقه‌مندی‌ها" icon={<Star size={18}/>} />
            <NavItem text="تاریخچه خدمت" icon={<History size={18}/>} />
            <NavItem text="تنظیمات" icon={<Settings size={18}/>} />
          </div>
        </div>

        </aside>

        <aside
          className={`hidden xl:block sticky top-4 z-30 h-[calc(100vh-2rem)] overflow-y-auto no-scrollbar transition-[width] duration-300 ${
            quickAccessCollapsed ? 'w-[28px]' : 'w-[306px]'
          }`}
        >
          <div
            className={`rounded-[2.5rem] relative transition-all duration-300 ${
              quickAccessCollapsed ? 'p-0 bg-white shadow-sm border border-gray-100 overflow-visible' : 'p-8 bg-white shadow-sm border border-gray-100 overflow-hidden'
            }`}
          >
            <div
              className={`absolute top-6 z-40 flex flex-col items-center gap-3 ${
                quickAccessCollapsed ? 'left-1/2 -translate-x-1/2' : 'left-6'
              }`}
            >
              <button
                type="button"
                onClick={() => setQuickAccessCollapsed(v => !v)}
                className="w-10 h-10 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center text-gray-500 hover:text-[#1A3062] hover:shadow-md transition"
                aria-label={quickAccessCollapsed ? 'باز کردن دسترسی سریع' : 'بستن دسترسی سریع'}
                title={quickAccessCollapsed ? 'باز کردن' : 'بستن'}
              >
                {quickAccessCollapsed ? <ChevronRight size={36} /> : <ChevronLeft size={18} />}
              </button>

              {!quickAccessCollapsed && (
                <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700">
                  <Grid size={20} />
                </div>
              )}
            </div>

            {quickAccessCollapsed ? (
              <div className="h-[calc(100vh-2rem)] relative">
                <div className="absolute inset-0">
                  <div className="h-full w-full bg-gradient-to-b from-[#f3f7ff] via-white to-[#f3f7ff]" />
                  <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(33,57,112,0.12),transparent_70%)]" />
                </div>
                <div className="absolute top-0 bottom-0 right-0 w-[2px] bg-[#e9eef7]" />

                <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
                  <div className="w-10 h-10 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700 shadow-sm">
                    <Grid size={18} />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="pr-10 pl-24">
                  <div className="text-right">
                    <h4 className="text-lg font-extrabold text-gray-800">دسترسی سریع و آسان</h4>
                    <p className="text-[11px] text-gray-400 mt-2">مجموعه‌ای از خدمات مهم</p>
                  </div>
                </div>

                <div className="space-y-6 mt-8">
                  <QuickAccessCard
                    title="تجارت"
                    desc="ثبت درخواست، تعرفه‌ها، قوانین و راهنما"
                    icon={<Building2 className="text-[#1A3062]" size={18} />}
                  />
                  <QuickAccessCard
                    title="حمل و نقل"
                    desc="انتخاب مسیر، زمان، هزینه و رهگیری"
                    icon={<Truck className="text-[#1A3062]" size={18} />}
                  />
                  <QuickAccessCard
                    title="سرمایه‌گذاری"
                    desc="مشاهده فرصت‌ها، مجوزها و ثبت علاقه‌مندی"
                    icon={<Landmark className="text-[#1A3062]" size={18} />}
                  />
                </div>
              </>
            )}
          </div>
        </aside>

        <main className="flex-1 min-w-0 flex flex-col h-screen overflow-hidden">
        
        <header className="bg-white h-20 shadow-sm flex items-center justify-between px-10 z-10 border-b border-gray-100">
           <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#213970] flex items-center justify-center text-white font-bold shadow-sm">
                 {currentUser?.fullName?.split(' ').map(n => n[0]).join('') || 'MS'}
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-800">{currentUser?.fullName || 'کاربر مهمان'}</div>
                <div className="text-[10px] text-gray-400">{currentUser?.username || 'شناسه کاربری'}</div>
                {!!currentUser?.roles?.length && (
                  <div className="text-[10px] text-gray-400 mt-1">{currentUser.roles.join('، ')}</div>
                )}
              </div>
           </div>
           
           <div className="flex items-center gap-4">
              <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-2xl border border-blue-100 leading-tight text-center">
                <div className="text-[11px] font-bold">{formatJalaliNow(new Date())}</div>
                <div className="text-[10px] text-blue-500/70 mt-1 font-semibold text-center">
                  {formatGregorian(new Date())}
                </div>
              </div>

              <div ref={notifBoxRef} className="relative">
                <button
                  type="button"
                  onClick={() => setNotificationsOpen((v) => !v)}
                  className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-gray-400 hover:text-blue-600 cursor-pointer transition border border-gray-100 relative"
                  aria-label="اعلان‌ها"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-white">
                      {unreadCount.toLocaleString('fa-IR')}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <div className="absolute top-12 left-0 w-[360px] bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="px-5 py-4 flex items-center justify-between">
                      <div className="text-right">
                        <div className="text-[12px] font-extrabold text-gray-800">اعلان‌ها</div>
                        <div className="text-[10px] text-gray-400 mt-1">
                          {unreadCount > 0 ? `${unreadCount.toLocaleString('fa-IR')} پیام خوانده‌نشده` : 'پیام جدیدی ندارید'}
                        </div>
                      </div>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={markNotificationsRead}
                          className="text-[11px] font-extrabold text-blue-700 bg-blue-50 border border-blue-100 px-3 py-2 rounded-2xl hover:bg-blue-100 transition"
                        >
                          خوانده شد
                        </button>
                      )}
                    </div>

                    <div className="border-t border-gray-100">
                      {sessionNotifications.slice(0, 3).map((n) => (
                        <div key={n.id} className="px-5 py-4 hover:bg-gray-50 transition">
                          <div className="text-[12px] font-bold text-gray-800">{n.title}</div>
                          <div className="text-[11px] text-gray-500 mt-1 leading-5">{n.body}</div>
                        </div>
                      ))}
                      {!sessionNotifications.length && (
                        <div className="px-5 py-6 text-[11px] text-gray-400">پیامی برای نمایش وجود ندارد.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center text-red-600 hover:bg-red-100 cursor-pointer transition border border-red-100"
                aria-label="خروج از سامانه"
                title="خروج"
              >
                <LogOut size={18} />
              </button>
           </div>
        </header>

          <div className="flex-1 p-8 overflow-y-auto bg-[#f8fafc] space-y-8">
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 flex overflow-hidden min-h-[450px]">
              <div className="flex-1 p-10 relative">
                <AnimatePresence mode='wait'>
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {activeTab === 'identity' && <IdentityContent user={currentUser} />}
                    {activeTab === 'residence' && <FavoritesContent />}
                    {activeTab === 'family' && <MyTasksContent />}
                    {activeTab === 'bills' && <FinancialContent />}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="w-24 bg-[#213970] flex flex-col items-center py-10 gap-6 shadow-inner">
                <TabButton id="identity" icon={<User size={24} />} active={activeTab} set={setActiveTab} label="اطلاعات کاربر" />
                <TabButton id="residence" icon={<Star size={24} />} active={activeTab} set={setActiveTab} label="مورد علاقه" />
                <TabButton id="family" icon={<Briefcase size={24} />} active={activeTab} set={setActiveTab} label="کارهای من" />
                <TabButton id="bills" icon={<Wallet size={24} />} active={activeTab} set={setActiveTab} label="مالی" />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-10">
              <div className="space-y-8 lg:col-span-2">
                <div className={`rounded-[2.5rem] p-10 text-white relative overflow-hidden shadow-xl min-h-[280px] flex flex-col justify-between border border-white/10 ${MENU_BACKGROUND_CLASS}`}>
                  <div className="relative z-10 flex justify-between items-start">
                    <h3 className="text-2xl font-bold">۵ دستگاه پرمخاطب کریدور</h3>
                    <div className="flex gap-6 text-[11px] text-blue-200">
                      <div className="flex items-center gap-2"><div className="w-2 h-2 bg-blue-400 rounded-full"></div> بنادر و دریانوردی</div>
                      <div className="flex items-center gap-2"><div className="w-2 h-2 bg-blue-400 rounded-full"></div> گمرک جمهوری اسلامی</div>
                    </div>
                  </div>
                  <div className="relative z-10 grid grid-cols-2 gap-4 mt-6">
                    {topFivePopularItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.title}
                          type="button"
                          data-route-title={item.title}
                          className="flex items-center gap-3 text-sm hover:text-yellow-400 cursor-pointer transition"
                        >
                          <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                            <Icon size={16} />
                          </div>
                          <span>{item.title}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="relative z-10 mt-8">
                    <div className="relative">
                      <input
                        type="text"
                        value={submenuSearchQuery}
                        onChange={(e) => setSubmenuSearchQuery(e.target.value)}
                        placeholder="با نوشتن حداقل یک حرف از زیرمنوها جستجو کنید"
                        className="w-full bg-white rounded-2xl py-4 pr-12 pl-4 text-gray-800 text-sm focus:outline-none"
                      />
                      <Search className="absolute right-4 top-4 text-gray-400" size={20} />
                    </div>
                    {normalizedQuery.length >= 1 && (
                      <div className="mt-3 bg-white/95 backdrop-blur rounded-2xl border border-white/30 overflow-hidden">
                        {submenuSearchResults.length ? (
                          <div className="max-h-56 overflow-auto">
                            {submenuSearchResults.map((item) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  key={item.title}
                                  type="button"
                                  data-route-title={item.title}
                                  className="w-full flex items-center gap-3 px-4 py-3 text-right text-sm text-gray-800 hover:bg-gray-50 transition"
                                >
                                  <div className="w-8 h-8 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700 flex-shrink-0">
                                    <Icon size={16} />
                                  </div>
                                  <span className="font-bold">{item.title}</span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="px-4 py-3 text-sm text-gray-500">موردی یافت نشد</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
    </div>
  );
};

const NavItem = ({ text, icon, onClick }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 px-4 py-3 text-[15px] text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200 shadow-[0_2px_10px_rgba(0,0,0,0.10)] hover:shadow-[0_3px_14px_rgba(0,0,0,0.14)]">
    {icon}
    <span className="font-medium">{text}</span>
  </button>
);

const SubMenuLabel = ({ text }) => (
  <div className="px-6 pt-2 pb-1 text-[11px] text-white/50 font-bold">{text}</div>
);

const SubNavItem = ({ text, icon, desc, level = 1 }) => {
  const paddingClass = level === 2 ? 'px-6' : 'px-4';
  const textClass = level === 2 ? 'text-[12px]' : 'text-[13px]';
  const descMrClass = level === 2 ? 'mr-9' : 'mr-7';

  return (
    <button
      type="button"
      data-route-title={text}
      className={`w-full flex flex-col ${paddingClass} py-2 hover:bg-white/5 rounded-lg cursor-pointer transition group shadow-[0_2px_10px_rgba(0,0,0,0.10)] hover:shadow-[0_3px_14px_rgba(0,0,0,0.14)]`}
    >
      <div className="flex items-center gap-3">
        <div className="text-white/50 group-hover:text-white transition">{icon}</div>
        <span className={`${textClass} text-white/70 group-hover:text-white transition`}>{text}</span>
      </div>
      {desc && <span className={`text-[10px] text-white/30 ${descMrClass} mt-0.5`}>{desc}</span>}
    </button>
  );
};

const TabButton = ({ id, icon, active, set, label }) => (
  <button onClick={() => set(id)} className={`w-full py-4 flex flex-col items-center justify-center transition-all duration-300 relative ${active === id ? 'text-white' : 'text-white/40 hover:text-white'}`}>
    {active === id && <motion.div layoutId="activeTabIndicator" className="absolute left-0 top-0 bottom-0 w-1.5 bg-yellow-400 rounded-r-full shadow-[0_0_15px_rgba(250,204,21,0.6)]" />}
    <div className={`p-3 rounded-2xl transition-all duration-300 ${active === id ? 'bg-white/10 scale-110' : ''}`}>{icon}</div>
    <span className="text-[10px] mt-2 font-bold">{label}</span>
  </button>
);

const QuickAccessCard = ({ title, desc, icon }) => (
  <div className="bg-[#fbfdff] rounded-[2rem] p-6 border border-gray-100 shadow-sm hover:shadow-md transition cursor-pointer">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-2xl bg-[#eef5ff] border border-[#dbeafe] flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="text-right">
        <h5 className="text-sm font-extrabold text-gray-800">{title}</h5>
        <p className="text-[11px] text-gray-400 leading-5 mt-2">{desc}</p>
      </div>
    </div>
  </div>
);

const IdentityContent = ({ user }) => (
   <div className="animate-fade-in">
   <div className="flex justify-between items-start mb-10">
      <div className="text-right">
         <h2 className="text-3xl font-extrabold text-[#1A3062]">اطلاعات هویتی</h2>
      </div>
      <div className="bg-blue-50 text-blue-700 px-6 py-2.5 rounded-2xl text-xs font-bold border border-blue-100 shadow-sm mt-2">
         {user?.type === 'legal' ? 'حقوقی' : 'حقیقی'} - احراز هویت سطح ۲
      </div>
   </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
         {user?.type === 'legal' ? (
           <>
             <InfoField label="نام شرکت" value={user?.companyName || user?.fullName || '—'} />
             <InfoField label="شناسه ملی شرکت" value={user?.nationalId || '—'} />
             <InfoField label="کد اقتصادی" value={user?.economicCode || '—'} />
             <InfoField label="شماره ثبت" value={user?.registrationNumber || '—'} />
             <InfoField label="شماره تلفن همراه" value={user?.mobile || '—'} />
             <InfoField label="پست الکترونیک" value={user?.email || '—'} />
              <InfoField label="نقش" value={user?.roles?.length ? user.roles.join('، ') : '—'} />
           </>
         ) : (
           <>
             <InfoField label="نام پدر" value={user?.fatherName || '—'} />
             <InfoField label="کد ملی" value={user?.nationalId || '—'} />
             <InfoField label="تاریخ تولد" value={user?.birthDate || '—'} />
             <InfoField label="شماره تلفن همراه" value={user?.mobile || '—'} />
             <InfoField label="ایمیل ملی" value={user?.email || '—'} />
              <InfoField label="نقش" value={user?.roles?.length ? user.roles.join('، ') : '—'} />
           </>
         )}
      </div>
   </div>
);

const FavoritesContent = () => {
  const favoriteTrackingCards = [
    {
      title: 'رهگیری چندوجهی (دریایی/ریلی/جاده‌ای/هوایی)',
      routeTitle: 'رهگیری چندوجهی (دریایی/ریلی/جاده‌ای/هوایی)',
      icon: Map,
      desc: 'رهگیری یکپارچه محموله در همه شیوه‌های حمل',
    },
    {
      title: 'رهگیری دریایی',
      routeTitle: 'دریایی',
      icon: Ship,
      desc: 'مشاهده وضعیت و مراحل محموله در حمل دریایی',
    },
    {
      title: 'رهگیری ریلی',
      routeTitle: 'ریلی',
      icon: Train,
      desc: 'پیگیری وضعیت عبور ایستگاهی و ETA در حمل ریلی',
    },
    {
      title: 'رهگیری جاده‌ای',
      routeTitle: 'جاده‌ای',
      icon: Truck,
      desc: 'رهگیری مسیر، توقف‌ها و زمان تحویل در حمل جاده‌ای',
    },
  ];

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-start mb-10">
        <div className="text-right">
          <h2 className="text-3xl font-extrabold text-[#1A3062]">مورد علاقه</h2>
          <p className="text-[11px] text-gray-400 mt-3">میانبرهایی که بیشتر استفاده می‌کنید یا ذخیره کرده‌اید.</p>
        </div>
        <div className="bg-yellow-50 text-yellow-700 px-6 py-2.5 rounded-2xl text-xs font-bold border border-yellow-100 shadow-sm mt-2 flex items-center gap-2">
          <Star size={16} />
          <span>پیشنهادی</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {favoriteTrackingCards.map((item) => {
          const Icon = item.icon || Star;
          return (
            <button
              key={item.title}
              type="button"
              data-route-title={item.routeTitle}
              className="bg-[#fbfdff] rounded-[2rem] p-6 border border-gray-100 shadow-sm hover:shadow-md transition cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#fff7ed] border border-[#ffedd5] flex items-center justify-center flex-shrink-0 text-[#b45309]">
                  <Icon size={18} />
                </div>
                <div className="text-right">
                  <h5 className="text-sm font-extrabold text-gray-800">{item.title}</h5>
                  <p className="text-[11px] text-gray-400 leading-5 mt-2">
                    {item.desc || 'دسترسی سریع به خدمت منتخب در کریدور'}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const MyTasksContent = () => {
  const tasks = [
    { title: 'پیگیری درخواست‌های ثبت‌شده', icon: History, status: 'در حال پیگیری', desc: 'مشاهده وضعیت درخواست‌ها در سامانه‌ها' },
    { title: 'تکمیل اطلاعات پرونده', icon: FileText, status: 'اقدام لازم', desc: 'مدارک/فرم‌ها را تکمیل و ارسال کنید' },
    { title: 'بررسی پیشنهادها و مقایسه خدمات', icon: Search, status: 'پیشنهادی', desc: 'مقایسه مسیر/زمان/هزینه خدمات' },
    { title: 'مرور پروژه‌ها و فیلتر', icon: Layout, status: 'پیشنهادی', desc: 'فیلتر و مقایسه پروژه‌ها و فرصت‌ها' },
  ];

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-start mb-10">
        <div className="text-right">
          <h2 className="text-3xl font-extrabold text-[#1A3062]">کارهای من</h2>
          <p className="text-[11px] text-gray-400 mt-3">کارهای جاری شما در کریدور: پیگیری‌ها، اقدام‌های لازم و پیشنهادها.</p>
        </div>
        <div className="bg-blue-50 text-blue-700 px-6 py-2.5 rounded-2xl text-xs font-bold border border-blue-100 shadow-sm mt-2 flex items-center gap-2">
          <Briefcase size={16} />
          <span>مرکز کارها</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tasks.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.title} className="bg-white rounded-[2rem] p-6 border border-gray-100 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#eef5ff] border border-[#dbeafe] flex items-center justify-center flex-shrink-0 text-[#1A3062]">
                    <Icon size={18} />
                  </div>
                  <div className="text-right">
                    <h5 className="text-sm font-extrabold text-gray-800">{t.title}</h5>
                    <p className="text-[11px] text-gray-400 leading-5 mt-2">{t.desc}</p>
                  </div>
                </div>
                <span className="text-[10px] font-bold bg-gray-50 border border-gray-100 text-gray-600 px-3 py-1.5 rounded-full whitespace-nowrap">
                  {t.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FinancialContent = () => {
  const cards = [
    { title: 'کیف پول', value: '—', icon: Wallet, desc: 'شارژ/برداشت (در صورت فعال بودن)' },
    { title: 'پرداخت‌ها', value: '—', icon: CreditCard, desc: 'تراکنش‌های موفق/معلق' },
    { title: 'فاکتورها و صورتحساب‌ها', value: '—', icon: FileText, desc: 'دانلود / چاپ / آرشیو' },
    { title: 'گزارش مالی', value: '—', icon: BarChart3, desc: 'خلاصه هزینه‌ها و وضعیت تسویه' },
  ];

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-start mb-10">
        <div className="text-right">
          <h2 className="text-3xl font-extrabold text-[#1A3062]">مالی</h2>
          <p className="text-[11px] text-gray-400 mt-3">درگاه یکپارچه برای کیف پول، پرداخت‌ها، فاکتورها و گزارش‌ها.</p>
        </div>
        <div className="bg-emerald-50 text-emerald-700 px-6 py-2.5 rounded-2xl text-xs font-bold border border-emerald-100 shadow-sm mt-2 flex items-center gap-2">
          <Wallet size={16} />
          <span>مدیریت مالی</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.title} className="bg-white rounded-[2rem] p-6 border border-gray-100 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0 text-emerald-700">
                  <Icon size={18} />
                </div>
                <div className="text-right flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h5 className="text-sm font-extrabold text-gray-800">{c.title}</h5>
                    <span className="text-[11px] font-extrabold text-gray-700">{c.value}</span>
                  </div>
                  <p className="text-[11px] text-gray-400 leading-5 mt-2">{c.desc}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 bg-[#fbfdff] rounded-[2rem] p-6 border border-gray-100 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#eef5ff] border border-[#dbeafe] flex items-center justify-center flex-shrink-0 text-[#1A3062]">
            <RefreshCw size={18} />
          </div>
          <div className="text-right">
            <h5 className="text-sm font-extrabold text-gray-800">تسویه حساب</h5>
            <p className="text-[11px] text-gray-400 leading-5 mt-2">
              وضعیت تسویه‌ها و درخواست تسویه جدید را از این بخش پیگیری کنید.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value }) => (
    <div className="relative">
       <label className="text-[10px] text-gray-400 block mb-2 mr-1">{label}</label>
       <div className="bg-[#fcfdfe] p-5 rounded-2xl border border-gray-100 font-bold text-gray-800 text-sm shadow-sm flex items-center justify-center">
          {value}
       </div>
    </div>
);

export default Dashboard;
